import { useEffect, useState } from "react"
import parse from "html-react-parser"

import { updateSubscriptionPlan, loadSubscriptionDetails } from "./api"

import Modal from "shared/components/modal"
import SuccessModal from "app/components/billing/success_modal"
import { saveUserPermissions } from "shared/scripts/new_badge_button"
import { CheckoutStep } from "../modal_copy"

import iftttLogo from "images/logo-ifttt.svg?raw"
import proPlusBadge from "images/plan-badges/pro_plus.svg?raw"

import styles from "../shared_modal.module.scss"

const PRO_PLUS_FEATURES = [
  "Unlimited Applets",
  "Fastest Applet speeds and increased rate limits",
  "AI, Twitter, Webhooks, SMS, and Phone Call services",
  "Queries and filter code",
  "Multiple accounts per service",
]

interface User {
  isTrialing: boolean
}
interface ProUpgradeModalProps {
  modalCopyObj: {
    copy: string
    editAppletWarning: string
    title: string
  }
  onClose: () => void
  onUpgrade: (checkoutModalTarget: string) => void
  proPlusProductId: string
  step: CheckoutStep
  trackLocation?: string
  user: User
  visible: boolean
}

const ProUpgradeModal = ({
  modalCopyObj,
  onClose,
  onUpgrade,
  proPlusProductId,
  step,
  trackLocation,
  user,
  visible,
}: ProUpgradeModalProps) => {
  const [proPrice, setProPrice] = useState("")
  const [loading, setLoading] = useState(false)
  const [upgrading, setUpgrading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const billingHeadsUp = user.isTrialing
    ? `${proPrice}Continue your trial with Pro+ to get access to queries, filter code, and multiple accounts.`
    : `${proPrice}Upgrade to get access to queries, filter code, and multiple accounts.`

  useEffect(() => {
    if (!proPrice) {
      setLoading(true)
      loadSubscriptionWithRetries(1).then(() => {
        setLoading(false)
      })
    }
  }, [])

  const loadSubscriptionWithRetries = (retries: number): Promise<void> => {
    return loadSubscriptionDetails("/billing/pro_upgrade_details")
      .then(detail => {
        setProPrice(`IFTTT Pro+ is billed at ${detail.formatted_pro_price} per ${detail.interval}. `)
      })
      .catch(error => {
        if (retries > 0) {
          return loadSubscriptionWithRetries(retries - 1)
        }
      })
  }

  const handleProUpgrade = async () => {
    setUpgrading(true)

    try {
      const { errors, user_subscription } = await updateSubscriptionPlan({ productId: proPlusProductId })
      if (!errors?.length && user_subscription.plan_id == "pro") {
        saveUserPermissions("pro", {
          filter_code: { permitted: true },
          multi_action: { permitted: true },
          queries: { permitted: true },
          multi_service_account: { permitted: true },
        })
        if (trackLocation && ["Settings", "Plans"].includes(trackLocation)) {
          setShowSuccess(true)
        } else {
          window.appendFlash("Upgrade complete. Welcome to IFTTT Pro+!", "success")
          onUpgrade("pro")
          onClose()
        }
      } else {
        const errorMessage =
          errors[0]?.message || "There was a problem upgrading your account. Please contact support for more information."
        window.appendFlash(errorMessage, "danger")
        onClose()
      }
    } catch {
      window.appendFlash("Something went wrong. Please try again.", "danger")
    } finally {
      setUpgrading(false)
    }
  }

  if (showSuccess) {
    return (
      <SuccessModal
        title="You've upgraded to"
        badge={iftttLogo + proPlusBadge}
        featuresLabel="Enjoy these new benefits"
        features={PRO_PLUS_FEATURES}
        visible={true}
        onClose={() => {
          onUpgrade("pro")
          onClose()
        }}
        buttons={[
          {
            label: "Back to Billing",
            onClick: () => {
              window.location.href = "/billing"
            },
            variant: "secondary",
          },
          {
            label: "Explore Applets",
            onClick: () => {
              window.location.href = "/explore"
            },
            variant: "primary",
          },
        ]}
      />
    )
  }

  const useDefaultCopy = ["plan_upgrade", "default_pro"].includes(step)
  const useDefaultTitle = useDefaultCopy || !modalCopyObj.title
  const title = useDefaultTitle ? "Get more with" : modalCopyObj.title

  return (
    <div className={styles["modal-container"]} data-fullscreen-modal>
      <Modal show={visible} closeListener={onClose} includeCloseX={true}>
        <h2 className="mt0 mb0">{title}</h2>
        {useDefaultTitle && <div className="success-modal-badge">{parse(iftttLogo + proPlusBadge)}</div>}
        {modalCopyObj.editAppletWarning && <p>{modalCopyObj.editAppletWarning}</p>}
        {!useDefaultCopy && modalCopyObj.copy && <p>{modalCopyObj.copy}</p>}
        <p>{billingHeadsUp}</p>
        <div className="action-buttons-row">
          <button className="button-tertiary button-outlined" onClick={onClose} disabled={upgrading}>
            Nevermind
          </button>
          <button className="button-tertiary" disabled={loading || upgrading} onClick={handleProUpgrade}>
            {upgrading ? "Upgrading..." : "Upgrade to Pro+"}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default ProUpgradeModal
